import React, { useMemo, useState } from "react";
import {
    Search,
    ChevronDown,
    Users,
    Star,
    IndianRupee,
    Building2,
    Mail,
    Layers,
} from "lucide-react";

const EXPERTS = [
    {
        id: "EXP-101",
        name: "Dr. Ananya Reddy",
        initials: "DA",
        domain: "Additive Manufacturing",
        affiliation: "IIT Tirupati",
        email: "ananya.reddy@iittp.ac.in",
        rating: 4.9,
        fee: 1500,
        status: "Active",
    },
    {
        id: "EXP-102",
        name: "Prof. Karthik Iyer",
        initials: "PK",
        domain: "Robotics & Mechatronics",
        affiliation: "IIIT Sri City",
        email: "karthik.iyer@iiits.in",
        rating: 4.8,
        fee: 1800,
        status: "Active",
    },
    {
        id: "EXP-103",
        name: "Meera Sundaram",
        initials: "MS",
        domain: "PCB & Embedded Design",
        affiliation: "APIC Visakhapatnam",
        email: "meera.sundaram@apic.gov.in",
        rating: 4.7,
        fee: 1200,
        status: "Active",
    },
    {
        id: "EXP-104",
        name: "Rahul Varma",
        initials: "RV",
        domain: "EV Powertrain",
        affiliation: "Ashok Leyland R&D",
        email: "rahul.varma@ashokleyland.com",
        rating: 4.9,
        fee: 2200,
        status: "Pending",
    },
    {
        id: "EXP-105",
        name: "Sunita Rao",
        initials: "SR",
        domain: "Industrial Design",
        affiliation: "NID Vijayawada",
        email: "sunita.rao@nid.edu",
        rating: 4.6,
        fee: 1000,
        status: "Active",
    },
    {
        id: "EXP-106",
        name: "Vikram Chowdary",
        initials: "VC",
        domain: "Composite Materials",
        affiliation: "APIC Guntur",
        email: "vikram.chowdary@apic.gov.in",
        rating: 4.5,
        fee: 1600,
        status: "Suspended",
    },
];

const STATUS_STYLES = {
    Active: "bg-green-100 text-green-700",
    Pending: "bg-amber-100 text-amber-700",
    Suspended: "bg-red-100 text-red-700",
};

function StatCard({ icon: Icon, label, value, tone }) {
    return (
        <div className="flex items-center gap-4 rounded-2xl border border-gray-200 bg-white p-5">
            <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tone}`}>
                <Icon className="h-5 w-5" />
            </span>
            <div>
                <p className="text-2xl font-bold text-gray-900">{value}</p>
                <p className="text-sm text-gray-500">{label}</p>
            </div>
        </div>
    );
}

export default function ExpertsManagement() {
    const [query, setQuery] = useState("");
    const [domainFilter, setDomainFilter] = useState("All Domains");
    const [statusFilter, setStatusFilter] = useState("All Status");

    const domains = useMemo(
        () => ["All Domains", ...new Set(EXPERTS.map((e) => e.domain))],
        []
    );
    const statuses = useMemo(
        () => ["All Status", ...new Set(EXPERTS.map((e) => e.status))],
        []
    );

    const counts = useMemo(() => {
        const active = EXPERTS.filter((e) => e.status === "Active").length;
        const avgRating = (
            EXPERTS.reduce((sum, e) => sum + e.rating, 0) / EXPERTS.length
        ).toFixed(1);
        const domainCount = new Set(EXPERTS.map((e) => e.domain)).size;
        return { total: EXPERTS.length, active, avgRating, domainCount };
    }, []);

    const filtered = useMemo(() => {
        return EXPERTS.filter((e) => {
            const matchesQuery =
                query.trim() === "" ||
                [e.name, e.domain, e.affiliation, e.email].some((field) =>
                    field.toLowerCase().includes(query.trim().toLowerCase())
                );
            const matchesDomain = domainFilter === "All Domains" || e.domain === domainFilter;
            const matchesStatus = statusFilter === "All Status" || e.status === statusFilter;
            return matchesQuery && matchesDomain && matchesStatus;
        });
    }, [query, domainFilter, statusFilter]);

    return (
        <div className="h-screen w-full overflow-y-auto bg-gray-50 scrollbar-hide">
            <style>{`
                .scrollbar-hide::-webkit-scrollbar {
                    display: none;
                }
                .scrollbar-hide {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
            `}</style>

            <div className="p-6">
                {/* Page heading */}
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-gray-900">Expert marketplace</h1>
                    <p className="mt-1 text-sm text-gray-500">
                        All experts registered to consult across Andhra Pradesh centres
                    </p>
                </div>

                {/* Stat cards */}
                <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <StatCard icon={Users} label="Total experts" value={counts.total} tone="bg-indigo-50 text-indigo-500" />
                    <StatCard icon={Star} label="Average rating" value={counts.avgRating} tone="bg-amber-50 text-amber-500" />
                    <StatCard icon={Layers} label="Domains covered" value={counts.domainCount} tone="bg-blue-50 text-blue-500" />
                    <StatCard icon={Users} label="Active experts" value={counts.active} tone="bg-green-50 text-green-500" />
                </div>

                {/* Table card */}
                <div className="w-full rounded-2xl border border-gray-200 bg-white shadow-sm">
                    {/* Header */}
                    <div className="flex flex-col gap-4 border-b border-gray-100 p-6 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h2 className="text-lg font-bold text-gray-900">Registered experts</h2>
                            <p className="mt-1 text-sm text-gray-500">
                                {filtered.length} of {EXPERTS.length} experts
                            </p>
                        </div>

                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                            <div className="relative">
                                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder="Search name, domain, affiliation..."
                                    className="w-64 rounded-lg border border-gray-200 bg-gray-50 py-2 pl-9 pr-3 text-sm text-gray-700 placeholder-gray-400 outline-none focus:border-gray-300 focus:bg-white"
                                />
                            </div>

                            <div className="relative">
                                <select
                                    value={domainFilter}
                                    onChange={(e) => setDomainFilter(e.target.value)}
                                    className="appearance-none rounded-lg border border-gray-200 bg-white py-2 pl-3 pr-8 text-sm text-gray-700 outline-none focus:border-gray-300"
                                >
                                    {domains.map((d) => (
                                        <option key={d} value={d}>
                                            {d}
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                            </div>

                            <div className="relative">
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    className="appearance-none rounded-lg border border-gray-200 bg-white py-2 pl-3 pr-8 text-sm text-gray-700 outline-none focus:border-gray-300"
                                >
                                    {statuses.map((s) => (
                                        <option key={s} value={s}>
                                            {s}
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[1050px] table-fixed border-collapse text-left">
                            <thead>
                                <tr className="bg-gray-50 text-xs uppercase tracking-wide text-gray-400">
                                    <th className="w-[20%] px-6 py-3 font-medium">Expert</th>
                                    <th className="w-[18%] px-6 py-3 font-medium">Domain</th>
                                    <th className="w-[18%] px-6 py-3 font-medium">Affiliation</th>
                                    <th className="w-[20%] px-6 py-3 font-medium">Email</th>
                                    <th className="w-[8%] px-6 py-3 font-medium">Rating</th>
                                    <th className="w-[10%] px-6 py-3 font-medium">Fee</th>
                                    <th className="w-[8%] px-6 py-3 font-medium">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((e, idx) => (
                                    <tr
                                        key={e.id}
                                        className={`text-sm ${idx !== filtered.length - 1 ? "border-b border-gray-100" : ""}`}
                                    >
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-xs font-semibold text-white">
                                                    {e.initials}
                                                </span>
                                                <div>
                                                    <p className="font-semibold text-gray-900">{e.name}</p>
                                                    <p className="text-xs text-gray-400">{e.id}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-gray-600">{e.domain}</td>
                                        <td className="px-6 py-4 text-gray-600">
                                            <div className="flex items-center gap-2">
                                                <Building2 className="h-4 w-4 shrink-0 text-gray-400" />
                                                <span className="truncate">{e.affiliation}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-gray-500">
                                            <div className="flex items-center gap-2">
                                                <Mail className="h-4 w-4 shrink-0 text-gray-400" />
                                                <span className="truncate">{e.email}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-gray-700">
                                            <div className="flex items-center gap-1">
                                                <Star className="h-4 w-4 shrink-0 fill-amber-400 text-amber-400" />
                                                {e.rating}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 font-medium text-gray-900">
                                            <div className="flex items-center gap-0.5">
                                                <IndianRupee className="h-3.5 w-3.5 shrink-0" />
                                                {e.fee.toLocaleString("en-IN")}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${STATUS_STYLES[e.status]}`}>
                                                {e.status}
                                            </span>
                                        </td>
                                    </tr>
                                ))}

                                {filtered.length === 0 && (
                                    <tr>
                                        <td colSpan={7} className="px-6 py-10 text-center text-sm text-gray-400">
                                            No experts match your filters.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}